import { create } from 'zustand';

// ============================================================================
// Image Library Store
// Shared project image library consumed by the Manage Images dialog and the
// ManageImagesTool. Replaces the tool-local in-memory map so images survive
// tool switches and can be managed from the UI.
// ============================================================================

export type ImageFormat = 'PNG' | 'JPG' | 'BMP' | 'TIFF' | 'GIF' | 'WEBP' | 'SVG';

export interface ProjectImage {
    id: string;
    name: string;
    /** URL usable in <img> (object URL for local files, path for links) */
    url: string;
    width: number;
    height: number;
    format: ImageFormat;
    usedIn: string[]; // Element IDs using this image
}

interface ImageLibraryState {
    images: ProjectImage[];

    addImage: (image: Omit<ProjectImage, 'id'> & { id?: string }) => ProjectImage;
    removeImage: (id: string) => boolean;
    reloadImage: (id: string) => void;
    markUsedIn: (id: string, elementId: string) => void;
}

const formatFromName = (name: string): ImageFormat => {
    const ext = name.split('.').pop()?.toUpperCase() ?? 'PNG';
    const allowed: ImageFormat[] = ['PNG', 'JPG', 'BMP', 'TIFF', 'GIF', 'WEBP', 'SVG'];
    return (allowed.includes(ext as ImageFormat) ? ext : 'PNG') as ImageFormat;
};

export const useImageLibraryStore = create<ImageLibraryState>((set, get) => ({
    images: [],

    addImage: (image) => {
        const id = image.id ?? crypto.randomUUID();
        const created: ProjectImage = {
            ...image,
            id,
            format: image.format ?? formatFromName(image.name),
        };
        set((state) => ({ images: [...state.images.filter((i) => i.id !== id), created] }));
        return created;
    },

    removeImage: (id) => {
        const image = get().images.find((i) => i.id === id);
        if (!image) return false;
        if (image.usedIn.length > 0) {
            console.warn(`Cannot remove image "${image.name}": in use by ${image.usedIn.length} element(s)`);
            return false;
        }
        set((state) => ({ images: state.images.filter((i) => i.id !== id) }));
        return true;
    },

    reloadImage: (id) => {
        const image = get().images.find((i) => i.id === id);
        if (image) {
            console.log(`Reloading image: ${image.name}`);
        }
    },

    markUsedIn: (id, elementId) =>
        set((state) => ({
            images: state.images.map((i) =>
                i.id === id && !i.usedIn.includes(elementId)
                    ? { ...i, usedIn: [...i.usedIn, elementId] }
                    : i
            ),
        })),
}));
